import { formatCurrency } from "@/lib/currency";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { cacheJobsForOffline, getCachedPendingJobs } from "@/lib/jobCacheHelper";
import { DesktopJobsTable } from "@/components/desktop/DesktopJobsTable";
import { isNative } from "@/lib/capacitor";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, MapPin, Calendar, CheckCircle2, Clock, Trash2, Filter, X, Search, ChevronLeft, ChevronRight, AlertCircle, Phone, User, XCircle, Edit, ArrowLeftRight, Building2, Navigation, Info, PlayCircle } from "lucide-react";
import { JobSummaryDialog, type JobSummaryData } from "@/components/JobSummaryDialog";
import { ReleaseKeyButton } from "@/components/ReleaseKeyButton";
import { formatAddressShort, openNavigation, joinAddressAndPostcode } from "@/lib/addressFormat";
import { resolveCollectionAddress, resolveCollectionPostcode, resolveDeliveryAddress, resolveDeliveryPostcode } from "@/lib/jobAddressHelpers";
import { sendDriverJobNotification } from "@/lib/sendDriverJobNotification";
import { DriverNotesBlock } from "@/components/DriverNotesBlock";
import { NavigationChoiceDialog } from "@/components/NavigationChoiceDialog";
import { JobStatusBadge, AppraisalStatusBadge } from "@/components/JobStatusBadge";
import { JobOfflineStatus } from "@/components/JobOfflineStatus";
import { UKRegPlate } from "@/components/UKRegPlate";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isValidPhoneNumber, formatPhoneToE164, extractPhoneCandidate, resolveCollectionCallPhone } from "@/lib/phoneUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompany } from "@/hooks/useCompany";
import { CancelJobModal } from "@/components/CancelJobModal";
import { DeleteJobDialog } from "@/components/DeleteJobDialog";

interface Job {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_registration: string;
  vin_number: string | null;
  delivery_address: string;
  delivery_postcode: string | null;
  status: string;
  created_at: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  collection_appraisal_id: string | null;
  collection_skipped: boolean | null;
  collection_completed_at: string | null;
  driver_id: string | null;
  is_draft: boolean;
  is_return_leg: boolean;
  has_return_vehicle: boolean;
  return_vehicle_registration: string | null;
  return_delivery_address: string | null;
  return_delivery_postcode: string | null;
  parent_job_id: string | null;
  delivery_contact: string;
  execution_type?: string;
  executing_company_id?: string | null;
  estimated_cost?: number | null;
  invoice_amount?: number | null;
  dispatch_notes: string | null;
  release_key_images: any;
  vehicle_appraisals: {
    completed_at: string | null;
  } | null;
  collection_address: string | null;
  collection_postcode: string | null;
  customers: {
    name: string;
    postcode: string | null;
    address: string;
    phone: string;
  } | null;
  drivers: {
    name: string;
  } | null;
}

interface Driver {
  id: string;
  name: string;
}

export const PendingJobsSection = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterDriver, setFilterDriver] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTargetJob, setCancelTargetJob] = useState<Job | null>(null);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [navDialogAddress, setNavDialogAddress] = useState("");
  const [navDialogLabel, setNavDialogLabel] = useState("");
  const [summaryJob, setSummaryJob] = useState<JobSummaryData | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null); // tracks which job id has an action in progress
  const { isAdmin, isDriver, isSuperAdmin, loading: roleLoading } = useUserRole();
  const canDeleteJob = (isAdmin || isSuperAdmin) && !isDriver;
  const { companyId } = useCompany();
  const isMobile = useIsMobile();
  const sectionRef = useRef<HTMLDivElement>(null);
  const fetchInFlightRef = useRef(false);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [subNames, setSubNames] = useState<Record<string, string>>({});
  const [subDriverNames, setSubDriverNames] = useState<Record<string, string>>({});
  const [parentJobInfo, setParentJobInfo] = useState<Record<string, { vehicle_make: string; vehicle_model: string; vehicle_registration: string; collection_address: string | null; delivery_address: string }>>({});

  const safeFormatDate = (value: string | null | undefined, pattern: string, fallback = "Not scheduled") => {
    if (!value) return fallback;
    try {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return fallback;
      return format(parsed, pattern);
    } catch {
      return fallback;
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── SINGLE combined effect for initial load + realtime + filter changes ──
  // Prevents the double-fetch that occurred when mount + filter useEffects both fired.
  useEffect(() => {
    if (roleLoading || !companyId) return;

    // On native, load from cache first for instant render
    if (isNative) {
      getCachedPendingJobs(companyId).then(cached => {
        if (cached.length > 0) {
          setJobs(cached as any);
          setLoading(false);
        }
      }).catch(() => {});
    }

    fetchDrivers();
    fetchPendingJobs();

    // Set up realtime with debounce to avoid fetch storms
    if (!companyId) return;

    const debouncedRefetch = () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => fetchPendingJobs(), 500);
    };

    const channel = supabase
      .channel('pending-jobs-updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` }, debouncedRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_appraisals', filter: `company_id=eq.${companyId}` }, debouncedRefetch)
      .subscribe();

    // Refetch on app resume / tab focus to clear stale cards
    const handleVisibility = () => {
      if (document.visibilityState === "visible") debouncedRefetch();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Refetch automatically when connectivity returns so the driver never has
    // to close and reopen the app after being offline.
    const handleOnline = () => debouncedRefetch();
    const handleNetworkStatusChange = (e: Event) => {
      const detail = (e as CustomEvent<{ connected?: boolean }>).detail;
      if (detail?.connected !== false) debouncedRefetch();
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("network-status-change", handleNetworkStatusChange as EventListener);

    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("network-status-change", handleNetworkStatusChange as EventListener);
    };
  }, [companyId, isAdmin, roleLoading, filterStatus, filterDriver, dateFrom, dateTo, searchTerm]);

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from("drivers")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  // ── Fetch with concurrent-request guard ──
  const fetchPendingJobs = async () => {
    // Prevent overlapping fetches from realtime bursts
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      let query = isAdmin
        ? supabase
            .from("jobs")
            .select(`
              id,
              vehicle_make,
              vehicle_model,
              vehicle_registration,
              vin_number,
              delivery_address,
              delivery_postcode,
              collection_address,
              collection_postcode,
              status,
              created_at,
              scheduled_date,
              scheduled_time,
              collection_appraisal_id,
              collection_skipped,
              collection_completed_at,
              driver_id,
              is_draft,
              is_return_leg,
              has_return_vehicle,
              return_vehicle_registration,
              return_delivery_address,
              return_delivery_postcode,
              parent_job_id,
              delivery_contact,
              estimated_cost,
              invoice_amount,
              dispatch_notes,
              release_key_images,
              execution_type,
              executing_company_id,
              vehicle_appraisals!collection_appraisal_id(completed_at),
              customers (name, postcode, address, phone),
              drivers (name)
            `)
        : supabase
            .from("jobs")
            .select(`
              id,
              vehicle_make,
              vehicle_model,
              vehicle_registration,
              vin_number,
              delivery_address,
              delivery_postcode,
              collection_address,
              collection_postcode,
              status,
              created_at,
              scheduled_date,
              scheduled_time,
              collection_appraisal_id,
              collection_skipped,
              collection_completed_at,
              driver_id,
              is_draft,
              is_return_leg,
              has_return_vehicle,
              return_vehicle_registration,
              return_delivery_address,
              return_delivery_postcode,
              parent_job_id,
              delivery_contact,
              dispatch_notes,
              release_key_images,
              execution_type,
              executing_company_id,
              vehicle_appraisals!collection_appraisal_id(completed_at),
              customers (name, postcode, address, phone),
              drivers (name)
            `);

      // Apply status filter
      if (filterStatus === "all") {
        query = query.in("status", ["pending", "in_progress"]);
      } else {
        query = query.eq("status", filterStatus);
      }
      
      // CRITICAL: Exclude jobs that have completed collection or skipped POC
      // A job should move out of pending once collection is done or skipped
      query = query.is("collection_completed_at", null);
      query = query.or("collection_skipped.is.null,collection_skipped.eq.false");

      // Apply driver filter
      if (filterDriver !== "all") {
        query = query.eq("driver_id", filterDriver);
      }

      // Apply date range filter
      if (dateFrom) {
        query = query.gte("created_at", dateFrom.toISOString());
      }
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte("created_at", endOfDay.toISOString());
      }

      const { data, error } = await query
        .order("scheduled_date", { ascending: true, nullsFirst: false })
        .order("scheduled_time", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      // Apply search filter on client side (filtering customer name, vehicle registration, and address)
      let filteredData = data || [];
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter(job => 
          job.vehicle_registration.toLowerCase().includes(searchLower) ||
          job.delivery_address.toLowerCase().includes(searchLower) ||
          (job.customers?.name && job.customers.name.toLowerCase().includes(searchLower))
        );
      }
      
      // Show jobs IMMEDIATELY — don't wait for sub-queries
      setJobs(filteredData);
      setLoading(false);

      // Cache jobs to IndexedDB for offline access on native
      cacheJobsForOffline(data || []);

      // Resolve supplementary data in background (non-blocking)
      resolveSupplementaryData(filteredData);
    } catch (error) {
      console.error("Error fetching pending jobs:", error);
      // On native, fall back to cached jobs when offline
      if (isNative) {
        try {
          const cached = await getCachedPendingJobs(companyId);
          if (cached.length > 0) {
            console.log(`[PendingJobs] Offline fallback: ${cached.length} cached jobs`);
            setJobs(cached as any);
          }
        } catch (cacheErr) {
          console.warn("[PendingJobs] Cache fallback failed:", cacheErr);
        }
      }
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  };

  /** Resolve subcontractor names, driver names, and parent job info without blocking UI */
  const resolveSupplementaryData = async (filteredData: any[]) => {
    try {
      // Resolve subcontractor company names
      const subCompanyIds = [...new Set(
        filteredData
          .filter((j: any) => j.execution_type === 'subcontracted' && j.executing_company_id)
          .map((j: any) => j.executing_company_id as string)
      )];
      if (subCompanyIds.length > 0) {
        const nameMap: Record<string, string> = {};
        await Promise.all(subCompanyIds.map(async (cid) => {
          try {
            const { data: info } = await supabase.rpc("get_linked_company_info", { p_company_id: cid });
            const row = Array.isArray(info) ? info[0] : info;
            if (row?.company_name) nameMap[cid] = row.company_name;
          } catch {}
        }));
        setSubNames(prev => ({ ...prev, ...nameMap }));
      }

      // Resolve driver names for subcontracted jobs where RLS hides the driver join
      const subDriverJobs = filteredData.filter(
        (j: any) => j.execution_type === 'subcontracted' && j.driver_id && !j.drivers
      );
      if (subDriverJobs.length > 0) {
        const driverNameMap: Record<string, string> = {};
        for (const job of subDriverJobs) {
          try {
            const { data: info } = await supabase.rpc("get_linked_company_info", { p_company_id: job.executing_company_id });
            const row = Array.isArray(info) ? info[0] : info;
            const companyName = row?.company_name || 'Subcontractor';
            driverNameMap[job.driver_id] = companyName + ' Driver';
          } catch {
            driverNameMap[job.driver_id] = 'Subcontractor Driver';
          }
        }
        setSubDriverNames(prev => ({ ...prev, ...driverNameMap }));
      }

      // Fetch parent job info for return legs
      const returnLegJobs = filteredData.filter((j: any) => j.is_return_leg && j.parent_job_id);
      if (returnLegJobs.length > 0) {
        const parentIds = [...new Set(returnLegJobs.map((j: any) => j.parent_job_id as string))];
        const { data: parentJobs } = await supabase
          .from('jobs')
          .select('id, vehicle_make, vehicle_model, vehicle_registration, collection_address, delivery_address')
          .in('id', parentIds);
        if (parentJobs) {
          const infoMap: Record<string, any> = {};
          parentJobs.forEach((p: any) => { infoMap[p.id] = p; });
          setParentJobInfo(prev => ({ ...prev, ...infoMap }));
        }
      }
    } catch (err) {
      console.warn("[PendingJobs] Supplementary data fetch failed (non-fatal):", err);
    }
  };

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterDriver("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const hasActiveFilters = filterStatus !== "all" || filterDriver !== "all" || dateFrom || dateTo || searchTerm;

  // Pagination calculations
  const totalJobs = jobs.length;
  const totalPages = Math.ceil(totalJobs / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentJobs = jobs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterDriver, dateFrom, dateTo, searchTerm, itemsPerPage]);

  const handleDeleteClick = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingJobId(jobId);
    setShowDeleteDialog(true);
  };

  /**
   * Delete Job — server-enforced permanent deletion.
   * ⚠️ Never routes through the Cancel Job / Abort workflow.
   */
  const handleJobDeleted = (jobId: string) => {
    setJobs(prev => prev.filter(j => j.id !== jobId));
    setDeletingJobId(null);
    fetchPendingJobs();
  };

  const handleAssignDriver = async (jobId: string, driverId: string) => {
    if (actionInFlight) return;
    setActionInFlight(jobId);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ driver_id: driverId })
        .eq('id', jobId);
      if (error) throw error;
      try {
        await supabase.functions.invoke('notify-driver-job-assigned', {
          body: { job_id: jobId, driver_id: driverId, notification_type: 'job_assigned' }
        });
      } catch {}
      toast.success("Driver assigned successfully");
      fetchPendingJobs();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error("Failed to assign driver");
    } finally {
      setActionInFlight(null);
    }
  };

  const handleReschedule = async (jobId: string, date: string, time: string | null) => {
    try {
      const updateData: any = { scheduled_date: date, scheduled_time: time };
      const { data: updatedJob, error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId)
        .select('driver_id, execution_type, executing_company_id, company_id')
        .single();
      if (error) throw error;
      toast.success("Schedule updated");
      fetchPendingJobs();

      // Notify the assigned driver about the schedule change
      if (updatedJob?.driver_id) {
        try {
          console.log('[Reschedule] Sending job_updated notification for job', jobId, 'driver', updatedJob.driver_id);
          await supabase.functions.invoke('notify-driver-job-assigned', {
            body: { job_id: jobId, driver_id: updatedJob.driver_id, notification_type: 'job_updated' },
          });
        } catch (notifyErr) {
          console.error('Failed to send job update notification:', notifyErr);
        }
      } else {
        console.log('[Reschedule] No driver assigned to job', jobId, '- skipping notification');
      }

      // Also notify subcontractor if job is subcontracted
      if (updatedJob?.executing_company_id && updatedJob?.execution_type === 'subcontracted') {
        try {
          await supabase.functions.invoke('notify-subcontractor-job-allocated', {
            body: { job_id: jobId, executing_company_id: updatedJob.executing_company_id, owner_company_id: updatedJob.company_id, notification_type: 'job_updated' },
          });
        } catch (subErr) {
          console.error('Failed to notify subcontractor:', subErr);
        }
      }
    } catch (error: any) {
      console.error('Error updating schedule:', error);
      toast.error("Failed to update schedule");
    }
  };

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/80 backdrop-blur mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Pending Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  

  const deletingJob = jobs.find(j => j.id === deletingJobId);

  return (
    <>
      <Card ref={sectionRef} className="border-border/50 bg-card/80 backdrop-blur mb-4 sm:mb-6 md:mb-8">
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
                <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                Pending Jobs
                <Badge variant="secondary" className="text-xs">{totalJobs}</Badge>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">Recently created jobs awaiting processing</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              {isAdmin && (
                <Button
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-xs sm:text-sm"
                >
                  <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Filters</span>
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">
                      {[filterStatus !== "all", filterDriver !== "all", dateFrom, dateTo].filter(Boolean).length}
                    </Badge>
                  )}
                </Button>
              )}
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => navigate(isAdmin ? "/jobs" : "/pending-jobs")}>
                View All
              </Button>
            </div>
          </div>

          {isAdmin && showFilters && (
            <div className="space-y-3 pt-4 border-t">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by vehicle registration, customer name, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Status</label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Driver</label>
                  <Select value={filterDriver} onValueChange={setFilterDriver}>
                    <SelectTrigger>
                      <SelectValue placeholder="All drivers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All drivers</SelectItem>
                      {drivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">From Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "MMM d, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">To Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "MMM d, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear all filters
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium mb-1">No pending jobs</p>
              <p className="text-sm">Jobs will appear here once they are created</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              {!isMobile && !isNative && isAdmin ? (
                <DesktopJobsTable
                  jobs={currentJobs}
                  drivers={drivers}
                  isAdmin={isAdmin}
                  variant="pending"
                  subNames={subNames}
                  subDriverNames={subDriverNames}
                  onAssignDriver={handleAssignDriver}
                  onReschedule={handleReschedule}
                  onEdit={(jobId) => navigate(`/create-job?jobId=${jobId}`)}
                  onCancel={(job) => { setCancelTargetJob(job); setCancelModalOpen(true); }}
                  onDelete={(jobId, e) => handleDeleteClick(jobId, e)}
                />
              ) : (
              <div className="space-y-3">
            {currentJobs.map((job) => {
              const hasAppraisal = job.vehicle_appraisals;
              const isAppraisalComplete = hasAppraisal && job.vehicle_appraisals?.completed_at;
              
              return (
                <div
                  key={job.id}
                  className="flex flex-col p-4 sm:p-5 rounded-xl border bg-background/60 hover:bg-background/90 transition-all group"
                >
                  {/* === SECTION 1: Job Identity === */}
                  <div
                    onClick={() => {
                      if (job.is_draft) {
                        navigate(`/create-job?jobId=${job.id}`);
                      } else {
                        navigate(`/resume-job/${job.id}`);
                      }
                    }}
                    className="cursor-pointer"
                  >
                    {/* Header: Vehicle + Status */}
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="native-card-title font-extrabold text-sm sm:text-base text-foreground leading-snug tracking-tight">
                        {job.is_return_leg ? `Return Vehicle – ${(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') && job.vin_number ? job.vin_number : job.vehicle_registration}` : `${job.vehicle_make} ${job.vehicle_model}`}
                      </h3>
                      <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                        <JobStatusBadge 
                          status={job.status}
                          isDraft={job.is_draft}
                          collectionCompleted={!!isAppraisalComplete}
                          collectionSkipped={job.collection_skipped || false}
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSummaryJob(job as unknown as JobSummaryData); }}
                          className="w-7 h-7 rounded-full border border-border/60 bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          aria-label="View job summary"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Second row: Reg plate + badges */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <UKRegPlate registration={(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') && job.vin_number ? job.vin_number : job.vehicle_registration} />
                      {job.execution_type === 'subcontracted' && (
                        <Badge variant="outline" className="border-primary/50 text-primary text-[10px]">
                          <Building2 className="w-3 h-3 mr-0.5" />
                          {job.executing_company_id && subNames[job.executing_company_id]
                            ? subNames[job.executing_company_id]
                            : 'Subcontracted'}
                        </Badge>
                      )}
                      {job.is_return_leg && (
                        <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 text-[10px]">
                          <ArrowLeftRight className="w-3 h-3 mr-0.5" />
                          RETURN
                        </Badge>
                      )}
                    </div>

                    {/* Meta rows */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <AppraisalStatusBadge 
                        type="POC" 
                        completed={!!isAppraisalComplete}
                        skipped={job.collection_skipped || false}
                      />
                      {job.customers?.name && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Package className="w-3 h-3 text-primary/60" />
                          {job.customers.name}
                        </span>
                      )}
                      {job.delivery_contact && (() => {
                        const name = job.delivery_contact.replace(/[\s]*[-–—]?\s*[\d\s+()]{10,}.*$/, '').trim() || job.delivery_contact.split(/[\d+]/)[0].trim();
                        return name && name !== job.customers?.name ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="w-3 h-3 text-primary/60" />
                            {name}
                          </span>
                        ) : null;
                      })()}
                    </div>

                    {/* --- Section divider --- */}
                    <div className="my-3 border-t border-border/30" />

                    {/* === SECTION 2: Address Block === */}
                    {(() => {
                      // SINGLE OWNER: resolveCollectionAddress / resolveDeliveryAddress (jobAddressHelpers.ts)
                      const collectAddr = resolveCollectionAddress(job);
                      const collectPostcode = resolveCollectionPostcode(job);
                      const deliverAddr = resolveDeliveryAddress(job);
                      const deliverPostcode = resolveDeliveryPostcode(job);

                      return (
                        <div className="rounded-lg bg-muted/40 border border-border/40 p-3 space-y-0">
                          {/* Collection */}
                          <div className="flex gap-2.5">
                            <div className="w-0.5 rounded-full bg-amber-500/60 flex-shrink-0 self-stretch" />
                            <div className="py-1">
                              <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-0.5">Collection</p>
                              <p className="text-xs sm:text-sm text-foreground break-words leading-snug">
                                {formatAddressShort(collectAddr, collectPostcode) || "—"}
                              </p>
                            </div>
                          </div>

                          <div className="border-t border-border/20 my-2" />

                          {/* Delivery */}
                          <div className="flex gap-2.5">
                            <div className="w-0.5 rounded-full bg-green-500/60 flex-shrink-0 self-stretch" />
                            <div className="py-1">
                              <p className="text-[10px] font-semibold uppercase tracking-widest text-green-600 dark:text-green-400 mb-0.5">Delivery</p>
                              <p className="text-xs sm:text-sm text-foreground break-words leading-snug">
                                {formatAddressShort(deliverAddr, deliverPostcode) || "—"}
                              </p>
                            </div>
                          </div>

                          {/* Driver Notes directly under Delivery */}
                          {job.dispatch_notes && (
                            <>
                              <div className="border-t border-border/20 my-2" />
                              <DriverNotesBlock notes={job.dispatch_notes} />
                            </>
                          )}

                          {/* Release Key Button */}
                          {Array.isArray(job.release_key_images) && job.release_key_images.length > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <ReleaseKeyButton images={job.release_key_images as string[]} />
                              <span className="text-[10px] text-muted-foreground">Release Key</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* --- Section divider --- */}
                    <div className="my-3 border-t border-border/30" />

                    {/* === SECTION 3: Execution Details === */}
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5 text-primary/60" />
                        {safeFormatDate(job.scheduled_date, "MMM d, yyyy", "Not scheduled")}
                        {job.scheduled_time && (
                          <span className="ml-0.5">@ {job.scheduled_time}</span>
                        )}
                      </span>
                      {isAdmin && (job.estimated_cost || job.invoice_amount) && (
                        <span className="text-sm sm:text-base font-extrabold text-foreground">
                          {formatCurrency(((job.invoice_amount ?? job.estimated_cost) || 0).toFixed(2))}
                        </span>
                      )}
                    </div>

                    {/* Return vehicle / Driver */}
                    {job.has_return_vehicle && !job.is_return_leg && (
                      <div className="flex items-center gap-2 mt-2 px-2.5 py-2 rounded-lg bg-accent/10 border border-accent/20">
                        <ArrowLeftRight className="w-4 h-4 flex-shrink-0 text-accent" />
                        <span className="text-xs font-semibold text-accent">🔄 Return Vehicle: {job.return_vehicle_registration || "N/A"}</span>
                        {job.return_delivery_address && (
                          <span className="text-xs text-accent/80 ml-1">→ {job.return_delivery_address}{job.return_delivery_postcode ? `, ${job.return_delivery_postcode}` : ''}</span>
                        )}
                      </div>
                    )}
                    {job.is_return_leg && job.parent_job_id && (
                      <div className="mt-2 px-2.5 py-2 rounded-lg bg-accent/10 border border-accent/20">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4 flex-shrink-0 text-accent" />
                          <span className="text-xs font-semibold text-accent">Return Leg of Outbound Job</span>
                        </div>
                        {parentJobInfo[job.parent_job_id] && (
                          <div className="mt-1.5 pl-6 text-xs text-muted-foreground space-y-0.5">
                            <p className="font-medium text-foreground">
                              🚗 {parentJobInfo[job.parent_job_id].vehicle_make} {parentJobInfo[job.parent_job_id].vehicle_model} — {parentJobInfo[job.parent_job_id].vehicle_registration}
                            </p>
                            <p>{parentJobInfo[job.parent_job_id].collection_address || 'N/A'} → {parentJobInfo[job.parent_job_id].delivery_address}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {job.drivers ? (
                      <div className="mt-2">
                        <Badge variant="secondary" className="text-xs font-normal text-muted-foreground">
                          Driver: {job.drivers.name}
                        </Badge>
                      </div>
                    ) : job.execution_type === 'subcontracted' && job.driver_id && subDriverNames[job.driver_id] ? (
                      <div className="mt-2">
                        <Badge variant="secondary" className="text-xs font-normal text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400">
                          {subDriverNames[job.driver_id]}
                        </Badge>
                      </div>
                    ) : null}
                    <div className="mt-1">
                      <JobOfflineStatus jobId={job.id} compact />
                    </div>
                  </div>

                  {/* --- Section divider --- */}
                  <div className="my-3 border-t border-border/30" />

                  {/* === SECTION 4: Actions === */}
                    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                    {(isMobile || isNative) && (
                      <Button
                        size="lg"
                        className="h-12 w-full text-base font-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(job.is_draft ? `/create-job?jobId=${job.id}` : `/resume-job/${job.id}`);
                        }}
                      >
                        {job.is_draft ? (
                          <><PlayCircle className="mr-2 h-5 w-5" />Continue Job Setup</>
                        ) : (
                          <><CheckCircle2 className="mr-2 h-5 w-5" />Complete Collection</>
                        )}
                      </Button>
                    )}

                    {(isMobile || isNative) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-12 w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          const collectAddr = resolveCollectionAddress(job);
                          const collectPostcode = resolveCollectionPostcode(job);
                          const addr = joinAddressAndPostcode(collectAddr, collectPostcode);
                          setNavDialogAddress(addr);
                          setNavDialogLabel("Navigate to Collection");
                          setNavDialogOpen(true);
                        }}
                      >
                        <Navigation className="w-4 h-4 mr-1.5" />
                        Navigate to Collection
                      </Button>
                    )}

                    {isMobile && (
                      <div className="flex flex-col gap-2">
                        {(() => {
                          // Priority: valid customer phone, then the valid job
                          // collection contact phone. Validation is unchanged.
                          const collectionPhoneToCall = resolveCollectionCallPhone(
                            job.customers?.phone,
                            job.delivery_contact,
                          );
                          
                          if (!collectionPhoneToCall) {
                            return (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="w-full h-10 text-xs border-primary/30 text-primary opacity-50 cursor-not-allowed"
                                disabled
                              >
                                <Phone className="w-4 h-4 mr-1" />
                                Call Collection
                              </Button>
                            );
                          }
                          
                          const formattedPhone = formatPhoneToE164(collectionPhoneToCall);
                          return (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full h-10 text-xs border-primary/30 text-primary hover:bg-primary/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `tel:${formattedPhone}`;
                              }}
                            >
                              <Phone className="w-4 h-4 mr-1" />
                              Call Collection
                            </Button>
                          );
                        })()}
                        {(() => {
                          const deliveryPhone = job.delivery_contact;
                          const phoneFromDelivery = deliveryPhone ? deliveryPhone.match(/[\d\s+()-]{10,}/)?.[0]?.trim() : null;
                          const deliveryPhoneToCall = isValidPhoneNumber(phoneFromDelivery) ? phoneFromDelivery : null;
                          
                          if (!deliveryPhoneToCall) {
                            return (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="w-full h-10 text-xs border-primary/30 text-primary opacity-50 cursor-not-allowed"
                                disabled
                              >
                                <Phone className="w-4 h-4 mr-1" />
                                Call Delivery
                              </Button>
                            );
                          }
                          
                          const formattedPhone = formatPhoneToE164(deliveryPhoneToCall);
                          return (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full h-10 text-xs border-primary/30 text-primary hover:bg-primary/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `tel:${formattedPhone}`;
                              }}
                            >
                              <Phone className="w-4 h-4 mr-1" />
                              Call Delivery
                            </Button>
                          );
                        })()}
                      </div>
                    )}

                    {(isAdmin || canDeleteJob) && (
                      <div className="space-y-2">
                        {isAdmin && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 h-9 text-xs border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                              disabled={!!actionInFlight}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/create-job?jobId=${job.id}`);
                              }}
                            >
                              <Edit className="h-3.5 w-3.5 mr-1" />
                              Edit Job
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 h-9 text-xs text-destructive border-destructive/50 hover:bg-destructive/10"
                              disabled={!!actionInFlight}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelTargetJob(job);
                                setCancelModalOpen(true);
                              }}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Cancel Job
                            </Button>
                          </div>
                        )}
                        {canDeleteJob && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-9 text-xs text-destructive border-destructive/50 hover:bg-destructive/10"
                            disabled={!!actionInFlight}
                            onClick={(e) => handleDeleteClick(job.id, e)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Delete Job
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
              )}

          {/* Pagination Controls */}
          {totalJobs > 0 && (
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <Select value={itemsPerPage.toString()} onValueChange={(val) => setItemsPerPage(Number(val))}>
                  <SelectTrigger className="w-16 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  {startIndex + 1}-{Math.min(endIndex, totalJobs)} of {totalJobs}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {!isMobile && (
                  <div className="flex items-center gap-1 px-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "ghost"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-8 h-8"
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                )}

                {isMobile && totalPages > 1 && (
                  <span className="text-sm text-muted-foreground px-2">
                    {currentPage} / {totalPages}
                  </span>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Job — permanent removal. Separate from Cancel Job / Abort. */}
      {canDeleteJob && (
        <DeleteJobDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          job={deletingJob ? {
            id: deletingJob.id,
            job_reference: (deletingJob as any).job_reference ?? null,
            vehicle_registration: deletingJob.vehicle_registration,
            status: deletingJob.status,
            collection_address: deletingJob.collection_address,
            delivery_address: deletingJob.delivery_address,
            customerName: deletingJob.customers?.name ?? null,
          } : null}
          onDeleted={handleJobDeleted}
          notify={(message, kind) => kind === "error" ? toast.error(message) : toast.success(message)}
        />
      )}

      {/* Cancel Job Modal */}
      {cancelTargetJob && (
        <CancelJobModal
          open={cancelModalOpen}
          onOpenChange={(val) => {
            setCancelModalOpen(val);
            if (!val) setCancelTargetJob(null);
          }}
          jobId={cancelTargetJob.id}
          vehicleReg={cancelTargetJob.vehicle_registration}
          defaultAbortFee={cancelTargetJob.invoice_amount ?? cancelTargetJob.estimated_cost ?? 0}
          onCancelled={fetchPendingJobs}
        />
      )}

      {/* Navigation Choice Dialog */}
      <NavigationChoiceDialog
        open={navDialogOpen}
        onOpenChange={setNavDialogOpen}
        address={navDialogAddress}
        label={navDialogLabel}
      />

      {/* Job Summary Dialog */}
      <JobSummaryDialog
        job={summaryJob}
        open={!!summaryJob}
        onOpenChange={(open) => { if (!open) setSummaryJob(null); }}
      />
    </>
  );
};
